class ProductMasonry extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.resizeTimeout = null;
    this.currentSlides = {};
  }

  connectedCallback() {
    // Wait for content to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
  }

  init() {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      this.setupHotspots();
      this.setupCarousels();
      this.setupAddToCart();
    });
  }

  setupHotspots() {
    const hotspots = this.querySelectorAll('[data-product-hotspot]');
    const overlay = this.querySelector('[data-popup-overlay]');

    if (hotspots.length === 0) {
      return;
    }

    // Remove existing event listeners by cloning and replacing
    hotspots.forEach((hotspot) => {
      const popupId = hotspot.getAttribute('data-popup-id');
      const popup = this.querySelector(`[data-popup="${popupId}"]`);

      if (!popup) {
        return;
      }

      // Clone to remove old listeners
      const newHotspot = hotspot.cloneNode(true);
      hotspot.parentNode.replaceChild(newHotspot, hotspot);

      // Open popup on hotspot click
      newHotspot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeAllPopups();
        popup.classList.add('active');
        if (overlay) overlay.classList.add('active');
        document.body.style.overflow = 'hidden';

        // Start autoscroll when modal opens
        if (popup.getAttribute('data-autoscroll-ready') === 'true') {
          this.startAutoScroll(popup);
        }
      });

      // Close popup on close button click
      const closeBtn = popup.querySelector('[data-popup-close]');
      if (closeBtn) {
        const newCloseBtn = closeBtn.cloneNode(true);
        closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);

        newCloseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeAllPopups();
        });
      }
    });

    // Close popup on overlay click
    if (overlay) {
      const newOverlay = overlay.cloneNode(true);
      overlay.parentNode.replaceChild(newOverlay, overlay);

      newOverlay.addEventListener('click', () => {
        this.closeAllPopups();
      });
    }

    // Close on escape key (only add once)
    if (!this.escapeListenerAdded) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAllPopups();
        }
      });
      this.escapeListenerAdded = true;
    }
  }

  closeAllPopups() {
    const popups = this.querySelectorAll('[data-product-popup]');
    const overlay = this.querySelector('[data-popup-overlay]');

    popups.forEach((popup) => {
      popup.classList.remove('active');
      // Stop autoscroll
      this.stopAutoScroll(popup);
      // Reset carousel to first slide when closing
      const popupId = popup.getAttribute('data-popup');
      if (this.currentSlides[popupId] !== undefined) {
        this.showSlide(popup, 0);
      }
    });
    if (overlay) overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  setupCarousels() {
    const modals = this.querySelectorAll('[data-product-popup]');

    modals.forEach((modal) => {
      const popupId = modal.getAttribute('data-popup');
      this.currentSlides[popupId] = 0;

      const prevBtn = modal.querySelector('[data-carousel-prev]');
      const nextBtn = modal.querySelector('[data-carousel-next]');
      const dots = modal.querySelectorAll('[data-carousel-dot]');
      const items = modal.querySelectorAll('.product-masonry__carousel-item');
      const carousel = modal.querySelector('[data-carousel]');

      if (items.length <= 1) return;

      // Button navigation
      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.changeSlide(modal, -1);
          this.resetAutoScroll(modal);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.changeSlide(modal, 1);
          this.resetAutoScroll(modal);
        });
      }

      // Dot navigation
      dots.forEach((dot) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const index = parseInt(dot.getAttribute('data-carousel-dot'));
          this.showSlide(modal, index);
          this.resetAutoScroll(modal);
        });
      });

      // Swipe functionality
      this.setupSwipe(carousel, modal);

      // Auto-scroll (will start when modal opens)
      modal.setAttribute('data-autoscroll-ready', 'true');
    });
  }

  setupSwipe(carousel, modal) {
    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartY = 0;
    let touchEndY = 0;

    carousel.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
        touchStartY = e.changedTouches[0].screenY;
      },
      { passive: true }
    );

    carousel.addEventListener(
      'touchend',
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        touchEndY = e.changedTouches[0].screenY;

        const deltaX = touchEndX - touchStartX;
        const deltaY = touchEndY - touchStartY;

        // Only trigger if horizontal swipe is more significant than vertical
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
          if (deltaX > 0) {
            // Swipe right - previous slide
            this.changeSlide(modal, -1);
          } else {
            // Swipe left - next slide
            this.changeSlide(modal, 1);
          }
          this.resetAutoScroll(modal);
        }
      },
      { passive: true }
    );
  }

  startAutoScroll(modal) {
    const popupId = modal.getAttribute('data-popup');
    const autoscrollInterval = parseFloat(modal.closest('product-masonry').dataset.autoscrollInterval || 2) * 1000;

    // Clear any existing interval
    if (modal.autoscrollTimer) {
      clearInterval(modal.autoscrollTimer);
    }

    // Don't start if interval is 0 or invalid
    if (autoscrollInterval <= 0) return;

    const items = modal.querySelectorAll('.product-masonry__carousel-item');
    if (items.length <= 1) return;

    modal.autoscrollTimer = setInterval(() => {
      // Only autoscroll if modal is active
      if (modal.classList.contains('active')) {
        this.changeSlide(modal, 1);
      }
    }, autoscrollInterval);
  }

  resetAutoScroll(modal) {
    if (modal.autoscrollTimer) {
      clearInterval(modal.autoscrollTimer);
    }
    this.startAutoScroll(modal);
  }

  stopAutoScroll(modal) {
    if (modal.autoscrollTimer) {
      clearInterval(modal.autoscrollTimer);
      modal.autoscrollTimer = null;
    }
  }

  changeSlide(modal, direction) {
    const popupId = modal.getAttribute('data-popup');
    const items = modal.querySelectorAll('.product-masonry__carousel-item');
    const totalSlides = items.length;

    this.currentSlides[popupId] = (this.currentSlides[popupId] + direction + totalSlides) % totalSlides;
    this.showSlide(modal, this.currentSlides[popupId]);
  }

  showSlide(modal, index) {
    const popupId = modal.getAttribute('data-popup');
    const items = modal.querySelectorAll('.product-masonry__carousel-item');
    const dots = modal.querySelectorAll('[data-carousel-dot]');

    this.currentSlides[popupId] = index;

    items.forEach((item, i) => {
      if (i === index) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });

    dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }

  setupAddToCart() {
    const addToCartButtons = this.querySelectorAll('[data-add-to-cart]');

    addToCartButtons.forEach((button) => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const variantId = button.getAttribute('data-add-to-cart');
        const originalText = button.textContent;

        button.textContent = 'Adding...';
        button.disabled = true;

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1,
                },
              ],
            }),
          });

          if (response.ok) {
            const data = await response.json();

            // Trigger cart update event
            document.dispatchEvent(
              new CustomEvent('cart:updated', {
                detail: {
                  cart: data,
                  source: 'product-masonry',
                },
              })
            );

            // Get and open the cart drawer
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              // Update cart drawer content
              const cartDrawerItems = document.querySelector('cart-drawer-items');
              if (cartDrawerItems) {
                cartDrawerItems.onCartUpdate();
              }

              // Open the drawer
              cartDrawer.open();
            }

            // Close modal
            this.closeAllPopups();
            button.textContent = originalText;
            button.disabled = false;
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          button.textContent = 'Error - Try Again';
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
        }
      });
    });
  }
}

if (!customElements.get('product-masonry')) {
  customElements.define('product-masonry', ProductMasonry);
}
